import {
  Button,
  Divider,
  Icon,
  Input,
  InputOnChangeData,
  Message,
  Modal,
} from "semantic-ui-react";
import React, { useEffect, useState } from "react";

import { QuestionCategory } from "../../Swagger/api";
import { localize } from "../../Localization/localize";
import { getApis } from "../../Services/webservice";

export interface IQuetionCategories {
  onCancel: () => void;
  onDone: () => void;
}

export const QuestionCategories = (props: IQuetionCategories) => {
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [items, setItems] = useState<QuestionCategory[]>([]);
  const [selectedForDelete, setSelectedForDelete] = useState<number>();
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [hasDeleteError, setHasDeleteError] = useState(false);
  const [usedItemsIds, setUsedItemsIds] = useState<number[]>([]);

  const { questionClient } = { ...getApis() };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setHasError(false);
      try {
        const [data, usedItemsData] = await Promise.all([
          questionClient.getQuestionCategories(),
          questionClient.getUsedQuestionCategoryIds(),
        ]);
        setItems(data);
        setUsedItemsIds(usedItemsData);
      } catch {
        setHasError(true);
      }
      setLoading(false);
    })();
  }, []);

  const { onCancel, onDone } = { ...props };

  const onInputChange = (
    data: InputOnChangeData,
    currentItem: QuestionCategory
  ) => {
    const copy = items.slice(0, items.length);
    for (const item of copy) {
      if (item.id == currentItem.id) {
        item.title = data.value;
      }
    }

    setItems(copy);
  };

  const onColorChange = (
    data: InputOnChangeData,
    currentItem: QuestionCategory
  ) => {
    const copy = items.slice(0, items.length);
    for (const item of copy) {
      if (item.id == currentItem.id) {
        item.color = data.value;
      }
    }

    setItems(copy);
  };

  const onActivateChange = (currentItem: QuestionCategory) => {
    const copy = items.slice(0, items.length);
    for (const item of copy) {
      if (item.id == currentItem.id) {
        item.deactivated = !currentItem.deactivated;
      }
    }

    setItems(copy);
  };

  const addNewItem = () => {
    const copy = items.slice(0, items.length);
    copy.push({
      deactivated: false,
      id: (items.length + 1) * -1,
      title: "",
      color: "",
    });
    setItems(copy);
  };

  const onSave = async () => {
    setLoading(true);
    setHasError(false);
    try {
      await questionClient.setQuestionCategories(items);
      onDone();
    } catch {
      setHasError(true);
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    setHasDeleteError(false);
    setLoadingDelete(true);
    try {
      // if the item was already saved on the server it has a correct id
      if (selectedForDelete && selectedForDelete > 0) {
        await questionClient.deleteQuestionCategory(selectedForDelete);
        const data = await questionClient.getQuestionCategories();
        setItems(data);
      } else {
        // otherwise just clear it from the state
        const data = [...items].filter((x) => x.id !== selectedForDelete);
        setItems(data);
      }

      setSelectedForDelete(undefined);
    } catch {
      setHasDeleteError(true);
    }
    setLoadingDelete(false);
  };

  const isItemUsed = (itemId: number) =>
    !!usedItemsIds.find((x) => x === itemId);

  return (
    <>
      <Modal dimmer="blurring" open>
        <Modal.Header>{localize.categories}</Modal.Header>
        <Modal.Content>
          {items.map((item) => (
            <React.Fragment key={item.id}>
              <Input
                value={item.title}
                fluid
                spellCheck="false"
                onChange={(_event, data) => {
                  onInputChange(data, item);
                }}
                action
              >
                <input />
                <Input
                  className="dd-sub-input"
                  value={item.color}
                  spellCheck="false"
                  onChange={(_event, data) => onColorChange(data, item)}
                />
                <Button
                  content={
                    item.deactivated
                      ? localize.activateLowerCase
                      : localize.deactivateLowerCase
                  }
                  onClick={() => {
                    onActivateChange(item);
                  }}
                />
                <Button
                  disabled={isItemUsed(item.id)}
                  onClick={() => setSelectedForDelete(item.id)}
                >
                  {localize.deleteLowerCase}
                </Button>
              </Input>
              <Divider horizontal hidden />
            </React.Fragment>
          ))}
          <Divider horizontal hidden />
          {hasError ? (
            <Message
              error
              content={localize.generalErrorMessage}
              header={localize.generalErrorHeader}
            />
          ) : null}
          <div className="dd-certificationCenters-footerMessage">
            <Divider horizontal hidden />
            {localize.questionCategoriesFooterMessage}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onCancel} floated="left">
            {localize.cancelLowerCase}
          </Button>
          <Button icon labelPosition="left" onClick={addNewItem}>
            <Icon name="plus" />
            {localize.insertLowerCase}
          </Button>

          <Button
            primary
            icon
            labelPosition="right"
            onClick={onSave}
            loading={loading}
          >
            <Icon name="save" />
            {localize.saveLowerCase}
          </Button>
        </Modal.Actions>
      </Modal>
      {selectedForDelete && (
        <Modal open dimmer="blurring">
          <Modal.Header>{localize.deleteUpperCase}</Modal.Header>
          <Modal.Content>
            <p>{localize.confirmCategoryDelete}</p>
            {hasDeleteError && (
              <Message
                error
                content={localize.generalErrorMessage}
                header={localize.generalErrorHeader}
              />
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button
              floated="left"
              onClick={() => setSelectedForDelete(undefined)}
            >
              {localize.cancelLowerCase}
            </Button>

            <Button
              primary
              icon
              labelPosition="right"
              onClick={deleteItem}
              loading={loadingDelete}
            >
              <Icon name="delete" />
              {localize.deleteLowerCase}
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};
